import { Link } from "@tanstack/solid-router";
import { For, Show } from "solid-js";

import { LeadScoreBadge } from "~/components/LeadScoreBadge";
import { useTopHotLeads } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
};

/**
 * Top-10 contacts by AI lead score (warm and up). Linked to detail
 * pages — designed for one-click triage from the dashboard.
 */
export function HotLeadsWidget(props: Props) {
  const leads = useTopHotLeads(props.wsKey);

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="card-title text-base">🔥 Hot leads</h3>
            <p class="text-xs opacity-60 mt-0.5">Top contacts by AI lead score (warm and up).</p>
          </div>
          <Link
            to="/workspaces/$wsKey/contacts"
            params={{ wsKey: props.wsKey() }}
            search={{ band: "hot" }}
            class="link link-primary text-xs"
          >
            View all →
          </Link>
        </div>

        <Show when={leads.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={!leads.isLoading && (leads.data?.items.length ?? 0) === 0}>
          <p class="text-sm opacity-60 mt-2">
            No scored contacts yet. Run "🎯 Score leads" from the Review Queue to populate.
          </p>
        </Show>

        <ul class="mt-2 divide-y divide-base-200">
          <For each={leads.data?.items ?? []}>
            {(c) => (
              <li class="py-2 flex items-center gap-2">
                <LeadScoreBadge score={c.lead_score} size="sm" />
                <Link
                  to="/workspaces/$wsKey/contacts/$conId"
                  params={{ wsKey: props.wsKey(), conId: c.id }}
                  class="link link-primary flex-1 truncate font-medium"
                >
                  {c.display_name ?? c.full_name}
                </Link>
                <span class="text-xs opacity-60 truncate hidden sm:inline">
                  {c.primary_email ?? ""}
                </span>
              </li>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
}
